import { ChatNavItem } from '../chat/companion/ChatNavItem';
import { ChatDetachMenu } from '../chat/companion/ChatDetachMenu';
import { AppInstallHelp } from '../chat/companion/AppInstallHelp';
import '../chat/companion/app-install-update.css';
import '../chat/companion/companion.css';
import { useReloadViewState } from '../lib/useReloadViewState';
import { nullableId } from '../lib/reloadViewState';
import { isFileBrowserTarget, isProjectBoardTarget } from '../lib/reloadViewTargets';
import { CallSettingsButton } from '../chat/calls/CallProvider';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useChat } from '../chat/ChatProvider';
import { ImageLightbox } from '../components/ImageLightbox';
import { ThemeToggle } from '../components/ThemeToggle';
import { AssetHoverPreview } from '../components/AssetHoverPreview';
import { LegacyIcon } from '../components/LegacyIcon';
import { AccountModal } from '../features/account/AccountModal';
import { AssetDetailsModal } from '../features/assets/AssetDetailsModal';
import { FileBrowserModal, type FileBrowserTarget } from '../features/files/FileBrowserModal';
import { MultiStateEditorModal } from '../features/state/MultiStateEditorModal';
import { StateEditorModal } from '../features/state/StateEditorModal';
import type { AppOutletContextValue, MultiStateSelection } from './AppOutletContext';
import { moveProject, orderedProjects, projectOrderKey, readProjectOrder, writeProjectOrder } from '../lib/projectOrder';
import { CreateDialog } from '../features/creation/CreateDialog';
import { useStructure } from '../features/creation/useStructure';
import { useHoldReorder } from '../features/creation/useHoldReorder';
import { EditStructureDialog } from '../features/creation/EditStructureDialog';
import type { StructureItem } from '../features/creation/structureApi';
import type { CreateKind } from '../features/creation/creationApi';
import { BellOff, Folder, Image as ImageIcon, Plus, SquarePen } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { boardAvatarApi } from '../features/board/boardAvatarApi';
import { ProjectDetailsModal, ProjectFolderModal, type ProjectBoardTarget } from '../features/board/ProjectDetailsModal';
import { useProjectFolderOpen } from '../features/board/useProjectFolderOpen';
import { useWindowSession } from './WindowSession';
import { RegulationLink } from './RegulationLink';
import { RegulationScroller } from './RegulationScroller';
import { ProjectContextMenu, type ProjectMenuTarget } from './ProjectContextMenu';
import { HideProjectDialog } from '../features/creation/HideProjectDialog';
import '../styles/workspace-windows.css';
import { DiskrepkaHeaderControls } from '../features/files/DiskrepkaHeaderControls';
import { useFolderOpen } from '../features/files/useFolderOpen';
import { FolderPicker } from '../features/files/FolderPicker';
import { TransferTray } from '../features/files/TransferTray';

export function AppShell() {
  const { user, logout } = useAuth();
  const { unreadCount } = useChat();
  const navigate = useNavigate();
  const location = useLocation();
  const { workspace, primary, companion } = useWindowSession();
  const [detachMenu,setDetachMenu]=useState<{x:number;y:number}|null>(null);
  const detached=companion?.phase==='native'||companion?.phase==='returning';
  const [createKind, setCreateKind] = useState<CreateKind | null>(null);
  const [editingRegulation, setEditingRegulation] = useState<StructureItem | null>(null);
  const [projectMenu, setProjectMenu] = useState(false);
  const [projectContext, setProjectContext] = useState<ProjectMenuTarget | null>(null);
  const [hidingProject, setHidingProject] = useState<{ id: number; title: string } | null>(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectBoard, setProjectBoard] = useReloadViewState<ProjectBoardTarget | null>('project-board', null, isProjectBoardTarget);
  const [projectFolder, setProjectFolder] = useReloadViewState<ProjectBoardTarget | null>('project-folder', null, isProjectBoardTarget);
  const [failedProjectImages, setFailedProjectImages] = useState<Record<number, string>>({});
  const projectCards = useQuery({ queryKey: ['project-cards'], queryFn: boardAvatarApi.projects, enabled: projectMenu && !!user && !user.chatOnly });
  const projectFolders = useProjectFolderOpen(user?.id ?? 0, setProjectFolder);
  const [projectOrders, setProjectOrders] = useState<Record<number, number[]>>({});
  const draggedProject = useRef<number | null>(null);
  const [draggingProject, setDraggingProject] = useState<number | null>(null);
  const [projectDrop, setProjectDrop] = useState<{ id: number; after: boolean } | null>(null);
  const [projectOrderError, setProjectOrderError] = useState('');
  const projectMenuRef = useRef<HTMLDivElement>(null);
  const projectButtonRef = useRef<HTMLButtonElement>(null);
  const [userMenu, setUserMenu] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [stateId, setStateId] = useReloadViewState<number | null>('task-card', null, nullableId);
  const [multiStateSelection, setMultiStateSelection] = useState<MultiStateSelection | null>(null);
  const [assetId, setAssetId] = useReloadViewState<number | null>('asset-card', null, nullableId);
  const [fileTarget, setFileTarget] = useReloadViewState<FileBrowserTarget | null>('asset-folder', null, isFileBrowserTarget);
  const folders = useFolderOpen(user?.id ?? 0, setFileTarget);

  const selectedProject = useMemo(() => {
    const match = location.pathname.match(/^\/projects\/(\d+)/);
    return user?.projects?.find((project) => project.id === Number(match?.[1]));
  }, [location.pathname, user?.projects]);

  const selectedRegulation = selectedProject?.regulations.find(regulation => location.pathname.endsWith(`/assets/${regulation.id}`));
  const canCreate = !!user?.canCreate && !user.chatOnly;
  const structure = useStructure('regulation', selectedProject?.id ?? 0, canCreate);
  useHoldReorder('.submenu-item[data-structure-id]', structure.query.data?.items.map(item => item.id) ?? [], canCreate && !structure.order.isPending, ids => structure.order.mutate(ids));
  useEffect(() => {
    document.title = workspace && selectedProject
      ? [selectedProject.title, selectedRegulation?.title].filter(Boolean).join(' · ')
      : 'skrepka';
  }, [workspace, selectedProject, selectedRegulation]);

  const openFolder = folders.open;
  const actions = useMemo<AppOutletContextValue>(() => ({
    openState: id => { if (!user?.chatOnly) setStateId(id); },
    openMultiState: selection => { if (!user?.chatOnly) setMultiStateSelection(selection); },
    openAsset: id => { if (!user?.chatOnly) setAssetId(id); },
    openFiles: (nextAssetId, title, paths) => { if (!user?.chatOnly) void openFolder(nextAssetId, title, paths); },
  }), [openFolder, user?.chatOnly, setStateId, setAssetId]);

  const signOut = async () => {
    await logout();
    void navigate('/auth/login', { replace: true });
  };

  useEffect(() => {
    const syncOrder = (event: StorageEvent) => {
      if (user && (event.key === projectOrderKey(user.id) || event.key === null)) {
        setProjectOrders(current => ({ ...current, [user.id]: readProjectOrder(user.id) }));
      }
    };
    window.addEventListener('storage', syncOrder);
    return () => window.removeEventListener('storage', syncOrder);
  }, [user]);

  useEffect(() => {
    const closeMenus = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest('.user-link')) setUserMenu(false);
      if (!target?.closest('.project-menu-button, .project-dropdown-menu, .project-context-menu')) { setProjectMenu(false); setProjectContext(null); }
    };
    const closeWithKeyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setUserMenu(false);
        setProjectMenu(false);
      }
    };
    document.addEventListener('mousedown', closeMenus, true);
    document.addEventListener('keydown', closeWithKeyboard);
    return () => {
      document.removeEventListener('mousedown', closeMenus, true);
      document.removeEventListener('keydown', closeWithKeyboard);
    };
  }, []);

  if (!user) return null;
  const dashboardSelected = location.pathname.startsWith('/dashboard');
  const dialogsSelected = location.pathname.startsWith('/dialogs');
  const savedProjectOrder = projectOrders[user.id] ?? readProjectOrder(user.id);
  const visibleProjects = orderedProjects((user.projects ?? []).filter((project) => project.regulations.length > 0 || user.canCreate), savedProjectOrder);
  const filteredProjects = visibleProjects.filter(
    (project) => project.title.toLocaleLowerCase('ru').includes(projectSearch.trim().toLocaleLowerCase('ru')),
  );
  const toggleProjectMenu = () => {
    if (!projectMenu) setProjectSearch('');
    setProjectMenu(!projectMenu);
    setProjectContext(null);
  };
  const closeProjectMenu = () => { setProjectMenu(false); setProjectContext(null); };
  const saveProjectOrder = (ids: number[]) => {
    setProjectOrders(current => ({ ...current, [user.id]: ids }));
    setProjectOrderError(writeProjectOrder(user.id, ids) ? '' : 'Браузер не сохранил порядок. Он действует до закрытия страницы.');
  };
  const endProjectDrag = () => {
    draggedProject.current = null;
    setDraggingProject(null);
    setProjectDrop(null);
  };
  const navigateProjectMenu = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      setProjectMenu(false);
      projectButtonRef.current?.focus();
      return;
    }
    const inSearch = event.target instanceof HTMLInputElement;
    if (inSearch && (event.key === 'Home' || event.key === 'End')) return;
    if (inSearch && event.key === 'Enter') {
      const first = filteredProjects[0];
      if (first) {
        setProjectMenu(false);
        void navigate(`/projects/${first.id}`);
      }
      event.preventDefault();
      return;
    }
    const links = [...(projectMenuRef.current?.querySelectorAll<HTMLAnchorElement>('a:not(.project-avatar)') ?? [])];
    if (!links.length) return;
    const index = links.indexOf(document.activeElement as HTMLAnchorElement);
    if (event.key === 'ArrowDown') links[(index + 1) % links.length]?.focus();
    else if (event.key === 'ArrowUp') links[(index <= 0 ? links.length : index) - 1]?.focus();
    else if (event.key === 'Home') links[0]?.focus();
    else if (event.key === 'End') links.at(-1)?.focus();
    else return;
    event.preventDefault();
  };
  const openProjectMenuFromKeyboard = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowDown') return;
    setProjectSearch('');
    setProjectMenu(true);
    event.preventDefault();
  };

  return (
    <div className="page">
      {detachMenu&&<ChatDetachMenu x={detachMenu.x} y={detachMenu.y} close={()=>setDetachMenu(null)}/>}
      {companion?.error&&<div className="chat-detach-notice" role="alert">{companion.error}{companion.launchFailed&&<AppInstallHelp/>}</div>}
      {!companion?.error&&companion?.notice&&<div className="chat-detach-notice" role="status">{companion.notice}</div>}
      <header className={`topline topline--clips${selectedProject ? ' topline--project-open' : ''}`}>
        <ul className="menu">
          {!user.chatOnly && <li className={`menu-item menu-item--tasks${dashboardSelected ? ' menu-item--selected' : ''}`}>
            <NavLink to="/dashboard/active"><span>Задачи</span></NavLink>
          </li>}
          <ChatNavItem detached={detached} selected={dialogsSelected} unreadCount={unreadCount} onContextMenu={event=>{if(!workspace){event.preventDefault();setDetachMenu({x:event.clientX,y:event.clientY});}}}/>
          {!user.chatOnly && <li className={`menu-item menu-item--project${selectedProject ? ' menu-item--selected' : ''}`}>
            {selectedProject ? <div className="project-capsule">
              <button className="project-menu-button" title={`Выбрать проект: ${selectedProject.title}`} onClick={toggleProjectMenu} onKeyDown={openProjectMenuFromKeyboard} ref={projectButtonRef} type="button">{selectedProject.title}</button>
              <div className="project-quick-actions" role="group" aria-label="Доска и папка проекта">
              <button type="button" className="project-board-action" aria-label={`Доска проекта: ${selectedProject.title}`} title="Открыть доску проекта" onClick={() => { closeProjectMenu(); setProjectBoard({ id: selectedProject.id, title: selectedProject.title }); }}><SquarePen size={16}/></button>
              <button type="button" className="project-folder-action" aria-label={`Корневая папка: ${selectedProject.title}`} title="Открыть корневую папку проекта" onClick={() => { closeProjectMenu(); void projectFolders.open({ id: selectedProject.id, title: selectedProject.title }); }}><Folder size={18} fill="currentColor"/></button>
              </div>
            </div> : <button className="project-menu-button" onClick={toggleProjectMenu} onKeyDown={openProjectMenuFromKeyboard} ref={projectButtonRef} type="button">Проекты</button>}
            {selectedProject ? (
              <RegulationScroller key={selectedProject.id} selectedId={selectedRegulation?.id}>
                {selectedProject.regulations.map((regulation) => (
                  <li
                    className={location.pathname.endsWith(`/assets/${regulation.id}`) ? 'submenu-item submenu-item--selected' : 'submenu-item'}
                    data-regulation-id={regulation.id}
                    data-structure-id={regulation.id}
                    key={regulation.id}
                  >
                    <RegulationLink projectId={selectedProject.id} regulationId={regulation.id} title={regulation.title} onEdit={canCreate ? () => setEditingRegulation(regulation) : undefined}/>
                  </li>
                ))}
                {user.canCreate && <li className="sc-regulation-add"><button type="button" className="sc-plus" aria-label="Добавить регламент" title="Добавить регламент" onClick={() => setCreateKind('regulation')}><Plus size={18}/></button></li>}
              </RegulationScroller>
            ) : null}
            {projectMenu ? (
              <div className="project-dropdown-menu" onKeyDown={navigateProjectMenu} ref={projectMenuRef}>
                <div className="sc-project-search-row"><label className="project-dropdown-search">
                  <LegacyIcon name="search" />
                  <input
                    autoFocus
                    onChange={(event) => setProjectSearch(event.target.value)}
                    placeholder="Найти проект"
                    value={projectSearch}
                  />
                </label>
                {canCreate && <button type="button" className="sc-project-create" aria-label="Создать проект" title="Создать проект" onClick={() => { setProjectMenu(false); setCreateKind('project'); }}><Plus size={20}/></button>}</div>
                <ul>
                  {filteredProjects.map((project) => {
                    const card = projectCards.data?.find(item => item.id === project.id);
                    return (
                    <li className={`project-order-row${draggingProject === project.id ? ' is-dragging' : ''}${projectDrop?.id === project.id ? (projectDrop.after ? ' drop-after' : ' drop-before') : ''}`} key={project.id}
                      onContextMenu={user.admin ? event => {
                        event.preventDefault();
                        const origin = event.currentTarget.querySelector<HTMLAnchorElement>('a:not(.project-avatar)')!;
                        const rect = origin.getBoundingClientRect();
                        setProjectContext({ id: project.id, title: project.title, x: event.clientX || rect.left, y: event.clientY || rect.bottom, origin });
                      } : undefined}
                      onKeyDown={user.admin ? event => {
                        if (event.key === 'ContextMenu' || event.key === 'F10' && event.shiftKey) {
                          event.preventDefault(); event.stopPropagation();
                          const origin = event.currentTarget.querySelector<HTMLAnchorElement>('a:not(.project-avatar)')!;
                          const rect = origin.getBoundingClientRect();
                          setProjectContext({ id: project.id, title: project.title, x: rect.left, y: rect.bottom, origin });
                        }
                      } : undefined}
                      onDragOver={(event) => {
                        if (draggedProject.current === null) return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'move';
                        const rect = event.currentTarget.getBoundingClientRect();
                        setProjectDrop({ id: project.id, after: event.clientY > rect.top + rect.height / 2 });
                      }}
                      onDrop={(event) => {
                        const moved = draggedProject.current;
                        if (moved === null) return;
                        event.preventDefault();
                        const rect = event.currentTarget.getBoundingClientRect();
                        saveProjectOrder(moveProject(visibleProjects.map(item => item.id), moved, project.id, event.clientY > rect.top + rect.height / 2));
                        endProjectDrag();
                      }}>
                      <button className="project-drag-handle" type="button" draggable aria-label={`Переместить проект ${project.title}`} title="Перетащите вверх или вниз. С клавиатуры — стрелками ↑ и ↓."
                        onDragStart={(event) => {
                          draggedProject.current = project.id;
                          setDraggingProject(project.id);
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData('text/plain', String(project.id));
                          if (event.currentTarget.parentElement) event.dataTransfer.setDragImage(event.currentTarget.parentElement, 16, 20);
                        }}
                        onDragEnd={endProjectDrag}
                        onKeyDown={(event) => {
                          if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
                          event.preventDefault(); event.stopPropagation();
                          const down = event.key === 'ArrowDown';
                          const adjacent = filteredProjects[filteredProjects.findIndex(item => item.id === project.id) + (down ? 1 : -1)];
                          if (adjacent) saveProjectOrder(moveProject(visibleProjects.map(item => item.id), project.id, adjacent.id, down));
                        }}><LegacyIcon name="move" /></button>
                      <NavLink className="project-avatar" data-asset-hover={project.title} data-avatar-original={card?.original || undefined} aria-label={`Выбрать проект: ${project.title}`} onClick={closeProjectMenu} to={`/projects/${project.id}`} tabIndex={-1}>
                        {card?.preview && failedProjectImages[project.id] !== card.preview ? <img src={card.preview} alt="" onError={() => setFailedProjectImages(current => ({ ...current, [project.id]: card.preview }))}/> : <ImageIcon size={22}/>}
                      </NavLink>
                      <NavLink onClick={closeProjectMenu} to={`/projects/${project.id}`}>{project.title}</NavLink>
                    </li>
                  ); })}
                  {!filteredProjects.length ? <li className="project-dropdown-empty">Проекты не найдены</li> : null}
                </ul>
                <div className="project-order-footer"><span>Перетаскивайте за ≡</span>{savedProjectOrder.length ? <button onClick={() => saveProjectOrder([])} type="button">По алфавиту</button> : null}</div>
                {projectOrderError ? <p className="form-error" role="alert">{projectOrderError}</p> : null}
              </div>
            ) : null}
          </li>}
        </ul>

        <div className="user-link">
          {!primary && <span className="workspace-window-indicator" title={workspace ? 'Окно регламента · звонки и уведомления в главном окне skrepka' : 'Звонки и уведомления в другом главном окне skrepka'} aria-label="Окно без звонков и уведомлений"><BellOff size={18}/></span>}
          {!user.chatOnly ? <DiskrepkaHeaderControls userId={user.id}/> : !dialogsSelected && <ThemeToggle/>}
          <button className="user-name" onClick={() => setUserMenu((value) => !value)} aria-expanded={userMenu} type="button">
            {user.initial}
          </button>
          <button className="diskrepka-account-menu" type="button" aria-label="Меню пользователя" aria-expanded={userMenu} onClick={() => setUserMenu(value => !value)}><LegacyIcon className="legacy-menu-icon" name="menu"/></button>
          <div className={`popup-menu${userMenu ? ' is-visible' : ''}`} onClick={() => setUserMenu(false)}>
            {user.admin ? <a className="popup-menu-item" href="/admin/project/" rel="noreferrer" target="_blank">Администрирование</a> : null}
            {user.admin ? <NavLink className="popup-menu-item" to="/settings/transfers">Передачи файлов</NavLink> : null}
            {user.accessManager || user.admin ? <NavLink className="popup-menu-item" to="/settings/users">Пользователи и доступ</NavLink> : null}
            {!user.chatOnly && <NavLink className="popup-menu-item" to="/attendance-v2">Посещаемость</NavLink>}
            <button className="popup-menu-item" onClick={() => setAccountOpen(true)} type="button">Настройки аккаунта</button>
            <CallSettingsButton className="popup-menu-item call-settings-item"/>
            <a className="popup-menu-item" href="/downloads/Skrepka-latest.apk" onClick={event=>{event.currentTarget.href='/downloads/Skrepka-latest.apk?t='+Date.now();}}>Скачать APK</a>
            <button className="popup-menu-item" onClick={() => void signOut()} type="button">Выйти</button>
          </div>
        </div>
      </header>

      <main className="legacy-app-content">
        <Outlet context={actions} />
      </main>

      {createKind && user.canCreate && <CreateDialog kind={createKind} projectId={selectedProject?.id} onClose={() => setCreateKind(null)}/>}
      {projectMenu && projectContext && user.admin && <ProjectContextMenu target={projectContext} onClose={() => setProjectContext(null)} onHide={() => { setHidingProject(projectContext); closeProjectMenu(); }}/ >}
      {hidingProject && user.admin && <HideProjectDialog project={hidingProject} onClose={() => setHidingProject(null)} onHidden={() => {
        if (projectBoard?.id === hidingProject.id) setProjectBoard(null);
        if (projectFolder?.id === hidingProject.id) setProjectFolder(null);
        if (selectedProject?.id === hidingProject.id) {
          setStateId(null); setMultiStateSelection(null); setAssetId(null); setFileTarget(null); setEditingRegulation(null); setCreateKind(null);
          void navigate('/dashboard/active', { replace: true });
        }
        setHidingProject(null);
      }}/ >}
      {editingRegulation && canCreate && selectedProject && <EditStructureDialog kind="regulation" item={editingRegulation} parentId={selectedProject.id} projectId={selectedProject.id} onClose={() => setEditingRegulation(null)}/>}
      {structure.order.error && <div className="sc-order-feedback" role="alert">{structure.order.error.message}<button type="button" aria-label="Закрыть сообщение" onClick={() => structure.order.reset()}>×</button></div>}
      {structure.order.isPending && <div className="sc-order-feedback" role="status">Сохраняем порядок для всех…</div>}
      <AccountModal onClose={() => setAccountOpen(false)} open={accountOpen} />
      {projectBoard && !user.chatOnly ? <ProjectDetailsModal key={projectBoard.id} target={projectBoard} onClose={() => setProjectBoard(null)}/> : null}
      {projectFolder && !user.chatOnly ? <ProjectFolderModal key={projectFolder.id} target={projectFolder} onClose={() => setProjectFolder(null)}/> : null}
      {!user.chatOnly && <><StateEditorModal onClose={() => setStateId(null)} stateId={stateId} />
      {multiStateSelection ? (
        <MultiStateEditorModal onClose={() => setMultiStateSelection(null)} selection={multiStateSelection} />
      ) : null}
      <AssetDetailsModal
        assetId={assetId}
        onClose={() => setAssetId(null)}
      />
      <FileBrowserModal onClose={() => setFileTarget(null)} target={fileTarget} />
      {folders.choice ? <FolderPicker target={folders.choice} onChoose={folders.choose} onClose={folders.cancelChoice}/> : null}
      </>}<ImageLightbox />
      {!user.chatOnly && <AssetHoverPreview />}
      {!user.chatOnly && <TransferTray/>}
      {folders.notice ? <div className="sx-opening-toast" role="status">{folders.notice}<button aria-label="Скрыть сообщение" onClick={folders.dismiss}>×</button></div> : null}
      {projectFolders.notice ? <div className="sx-opening-toast" role="status">{projectFolders.notice}<button aria-label="Скрыть сообщение" onClick={projectFolders.dismiss}>×</button></div> : null}
    </div>
  );
}
import '../features/files/user-password.css';
import '../features/files/button-contrast.css';
import '../features/files/chat-only.css';
